import { useState } from 'react';
import { Eye, ArrowRight, AlertTriangle } from 'lucide-react';
import { buildPreviewBreakdown, ReportSummaryKind, assertNever } from '@signalvote/shared';
import type { PreviewBreakdownGroup, ComplianceReportDetailDto } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useReportPreview, useFilingPreflight } from '@/hooks/queries/useFinanceReports';
import { useComplianceProfile } from '@/hooks/queries/useFinanceSettings';
import { cn } from '@/lib/utils';
import { formatAmount, formatDate } from '../utils';
import { Form3SummaryView } from '../reports/Form3SummaryView';
import { VaCometSummaryView } from '../reports/VaCometSummaryView';
import { AlabamaSummaryView } from '../reports/AlabamaSummaryView';
import { NorthCarolinaSummaryView } from '../reports/NorthCarolinaSummaryView';
import { portalNameFor } from '../reports/amendmentWorkflowHint';

/**
 * CPH-014 "Preview anytime" affordance. Surfaces below the Phase 3 step card
 * regardless of gate state — when the gate is locked the treasurer can still
 * see what the report would look like *right now* to inform triage. Opens a
 * read-only dialog rendered against `useReportPreview(deadlineId)`'s by-
 * deadline preview shape so no `ComplianceReport` has to exist yet.
 *
 * The breakdown is deliberately JURISDICTION-NEUTRAL (issue #171): it groups the
 * period's staged transactions by contribution type / expenditure category via
 * the shared `buildPreviewBreakdown` helper, so AL/VA/LA/FEC (and any future
 * jurisdiction) all get the same richer breakdown with no per-state branch here.
 * The full, per-jurisdiction schedule view (Form 3 / COMET / FCPA) is reached by
 * the "View the full report" link once a report has been generated — the existing
 * report-detail view is reused, not rebuilt.
 *
 * The dialog never enters the filing flow — no preflight modal, no
 * attestation, no audit-log writes. Filing is gated at the action layer by
 * the existing FPD-002 attestation modal, which only mounts from the file
 * sub-route, not here.
 */
export interface PreviewReportButtonProps {
  deadlineId: string;
  /**
   * The campaign's ruleset code (`CampaignFinanceSettings.RulesetCode`, e.g.
   * "AL"). Selects the jurisdiction-accurate expenditure labels; omit for the
   * federal-flavored defaults.
   */
  rulesetCode?: string | null;
}

export function PreviewReportButton({ deadlineId, rulesetCode }: PreviewReportButtonProps) {
  const [open, setOpen] = useState(false);
  const { data: preview, isLoading, isError } = useReportPreview(
    open ? deadlineId : null,
  );
  const { data: profile } = useComplianceProfile();
  // The compliance preflight for this period's report, once one exists. Read from the SAME
  // authority the Review & file step uses rather than recomputing anything here — a heading
  // that says "Before you file" while omitting an over-limit contribution is a false promise,
  // and a second copy of the rule would drift from the first.
  const { data: preflight } = useFilingPreflight(preview?.reportId ?? null);
  const preflightWarnings = preflight?.warnings ?? [];

  const breakdown = preview
    ? buildPreviewBreakdown(preview, rulesetCode)
    : null;

  // ADR-051 — when the server projected the real state-specific view for this period (run against
  // a virtual report, BEFORE any report is generated), render it read-only. Otherwise fall back to
  // the jurisdiction-neutral breakdown below. A degraded federal summary carries
  // `summaryKind === Flat`, so it correctly renders the neutral view here too.
  const stateSummaryView =
    preview?.detail && preview.detail.summaryKind !== ReportSummaryKind.Flat
      ? renderStateSummaryView(preview.detail, profile?.employerOccupationRequired ?? false)
      : null;

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="phase-3-preview-report-button"
        onClick={() => setOpen(true)}
        className="self-start"
      >
        <Eye className="h-4 w-4" aria-hidden="true" />
        Preview report
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className={cn(
            'max-h-[85vh] overflow-y-auto',
            // The state-specific summary views (Form 3 / COMET / FCPA) need document width; the
            // compact neutral breakdown keeps the default modal width.
            stateSummaryView && 'sm:max-w-3xl',
          )}
        >
          <DialogHeader>
            <DialogTitle>Report preview</DialogTitle>
            <DialogDescription>
              Read-only view of the period as it stands now. When it looks
              right, file it from this page.
            </DialogDescription>
          </DialogHeader>
          {isLoading && (
            <p
              data-testid="preview-loading"
              className="py-6 text-center text-sm text-muted-foreground"
            >
              Loading preview…
            </p>
          )}
          {isError && (
            <p
              role="alert"
              data-testid="preview-error"
              className="py-6 text-center text-sm text-destructive"
            >
              Couldn't load preview.
            </p>
          )}
          {preview && breakdown && (
            <div
              data-testid="preview-content"
              className="flex flex-col gap-4 py-2"
            >
              <div className="flex items-baseline justify-between">
                <h3 className="text-base font-semibold">{preview.periodName}</h3>
                <span className="text-xs text-muted-foreground">
                  {formatDate(preview.periodStart)} – {formatDate(preview.periodEnd)}
                </span>
              </div>

              {/* Cash position + headline activity — the four figures the compact
                  view always showed, kept as the at-a-glance summary. */}
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-muted-foreground">Contributions</dt>
                  <dd className="font-medium">
                    {preview.contributionCount} · {formatAmount(preview.totalContributions)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Expenditures</dt>
                  <dd className="font-medium">
                    {preview.expenditureCount} · {formatAmount(preview.totalExpenditures)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Beginning balance</dt>
                  <dd className="font-medium">
                    {formatAmount(preview.beginningBalance)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Ending balance</dt>
                  <dd className="font-medium">
                    {formatAmount(preview.endingBalance)}
                  </dd>
                </div>
              </dl>

              {/* ADR-051 — the real per-jurisdiction summary view (Form 3 / COMET / FCPA), projected
                  server-side against a virtual report so it renders BEFORE generation, replaces the
                  neutral breakdown when present. Read-only: the edit / fix-info affordances are
                  disabled (the fixes live in the close inbox, reached via "View the full report").
                  When the server had no state-specific view (un-onboarded jurisdiction, or a summary
                  that couldn't build), it falls back to the jurisdiction-neutral breakdown below. */}
              {stateSummaryView ? (
                <div data-testid="preview-state-summary">{stateSummaryView}</div>
              ) : (
                <>
                  {/* Jurisdiction-neutral breakdown — receipts by type, disbursements
                      by category. Renders for every jurisdiction from the same helper. */}
                  {breakdown.contributionGroups.length > 0 && (
                    <BreakdownSection
                      testId="preview-receipts"
                      title="Receipts by type"
                      groups={breakdown.contributionGroups}
                    />
                  )}
                  {breakdown.expenditureGroups.length > 0 && (
                    <BreakdownSection
                      testId="preview-disbursements"
                      title="Disbursements by category"
                      groups={breakdown.expenditureGroups}
                    />
                  )}
                </>
              )}

              {/* Informational, read-only — the fixes (and the Attest / Dismiss actions on the
                  compliance warnings) live in the close inbox and the Review & file step. */}
              {(preview.warnings.length > 0 || preflightWarnings.length > 0) && (
                <div
                  data-testid="preview-warnings"
                  className="rounded-lg border border-amber-200 bg-amber-50 p-3"
                >
                  <p className="flex items-center gap-1.5 text-sm font-medium text-amber-900">
                    <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                    Before you file
                  </p>
                  <ul className="mt-1.5 space-y-1 text-sm text-amber-800">
                    {preview.warnings.map((w, idx) => (
                      <li key={idx}>
                        {w.message}
                        {w.count > 0 ? ` (${w.count})` : ''}
                      </li>
                    ))}
                    {preflightWarnings.map((w) => (
                      <li key={w.warningId}>{w.warningText}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* The full per-jurisdiction schedule view (Form 3 / COMET / FCPA)
                  lives on the report detail page. Reuse it via a prominent link
                  once a report exists — never a parallel summary rebuilt here. A
                  plain anchor (not the router) mirrors FilePreflightModal's
                  "View receipt" jump: a deliberate full navigation out of the
                  modal into the report detail route. */}
              {preview.reportId && (
                <a
                  href={`/admin/finance/reports/${preview.reportId}`}
                  data-testid="preview-view-full-report"
                  className="inline-flex items-center gap-1 self-start text-sm font-medium text-primary hover:underline"
                >
                  View the full report
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * ADR-051 — dispatch the server-resolved `summaryKind` to the SAME summary view the report-detail
 * page renders (reused, never rebuilt), so the preview and the detail page show one projection.
 * Read-only: edit / fix-info callbacks are disabled here. `Flat` never reaches this (the caller
 * renders the neutral breakdown for it), but the exhaustive `assertNever` keeps a new
 * `ReportSummaryKind` a compile error until it's wired here — mirroring the report-detail switch.
 */
function renderStateSummaryView(
  detail: ComplianceReportDetailDto,
  requiresEmployer: boolean,
) {
  const noop = () => {};
  switch (detail.summaryKind) {
    case ReportSummaryKind.Federal:
      return (
        <Form3SummaryView
          summary={detail.federalSummary!}
          contributionItems={detail.contributionItems}
          expenditureItems={detail.expenditureItems}
          requiresEmployer={requiresEmployer}
          onEditContribution={null}
          onEditExpenditure={null}
          onFixMissingInfo={noop}
          // The Close-hub preview is always a not-yet-filed period, by construction.
          asFiled={false}
        />
      );
    case ReportSummaryKind.Virginia:
      return (
        <VaCometSummaryView
          vaSchedules={detail.vaSchedules!}
          contributionItems={detail.contributionItems}
          expenditureItems={detail.expenditureItems}
          refundItems={detail.refundItems}
          onEditContribution={null}
          onEditExpenditure={null}
        />
      );
    case ReportSummaryKind.Alabama:
      return (
        <AlabamaSummaryView
          report={detail}
          preview={null}
          portalName={portalNameFor(detail.stateCode)}
          requiresEmployer={requiresEmployer}
          onEditContribution={null}
          onEditExpenditure={null}
          onFixMissingInfo={noop}
        />
      );
    case ReportSummaryKind.NorthCarolina:
      // #304 — the NC CFRS Detailed Summary (CRO-1100) cover, off the derived totals the preview's
      // virtual-report projection already footed. The compact preview shows the cover only; the
      // itemized schedules live on the report detail page ("View the full report").
      return <NorthCarolinaSummaryView summary={detail.northCarolinaSummary!} />;
    case ReportSummaryKind.Flat:
      return null;
    default:
      return assertNever(detail.summaryKind);
  }
}

function BreakdownSection({
  testId,
  title,
  groups,
}: {
  testId: string;
  title: string;
  groups: PreviewBreakdownGroup[];
}) {
  return (
    <div data-testid={testId}>
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {title}
      </p>
      <ul className="mt-1.5 divide-y text-sm">
        {groups.map((g) => (
          <li key={g.key} className="flex items-baseline justify-between py-1">
            <span>
              {g.label}
              <span className="ml-1.5 text-xs text-muted-foreground">
                {g.count} {g.count === 1 ? 'item' : 'items'}
              </span>
            </span>
            <span className="font-medium">{formatAmount(g.total)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
